import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash, Warning, X } from "@phosphor-icons/react";
import { clearUserId } from "@/lib/userId";

/**
 * Danger-zone "Delete account" panel rendered at the bottom of Settings.
 *
 * Flow:
 *   1. User taps "Delete my account" → confirmation modal opens.
 *   2. User must type "DELETE" exactly to enable the destroy button.
 *   3. Tap "Permanently delete" → calls DELETE /api/account with the current
 *      X-User-Id (auto-injected by the global interceptor), wipes localStorage,
 *      and hard-reloads back to onboarding.
 */
export default function DeleteAccountSection({ api, profile }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => {
    if (busy) return;
    setOpen(false);
    setConfirm("");
  };

  const handleDelete = async () => {
    if (confirm !== "DELETE") return;
    setBusy(true);
    try {
      const res = await axios.delete(`${api}/account`);
      const removed = res?.data?.docs_removed ?? 0;
      // Wipe the local user id so a fresh device:UUID is minted on reload.
      clearUserId();
      try {
        // Also nuke local UI prefs that wouldn't make sense without an account.
        localStorage.removeItem("uptop-legacy-claimed");
      } catch (e) { /* ignore */ }
      toast.success(`Account deleted (${removed} records removed)`);
      // Slight delay so the toast is visible before the reload.
      setTimeout(() => { window.location.href = "/"; }, 700);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't delete account. Try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't delete account. Try again.");
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-red-500/30 bg-red-500/5 p-5"
      data-testid="delete-account-section"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 flex items-center justify-center border border-red-500/40 text-red-400 shrink-0">
          <Warning size={20} weight="bold" />
        </div>
        <div className="min-w-0">
          <h3
            className="text-sm font-bold tracking-[0.1em] uppercase text-red-300"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Danger zone
          </h3>
          <p className="text-xs text-[#A1A1AA] mt-1 leading-relaxed">
            Permanently delete your UpTop account and every workout, template, body
            measurement and personal record tied to it. This cannot be undone.
          </p>
        </div>
      </div>

      <button
        onClick={() => setOpen(true)}
        className="border border-red-500/40 text-red-300 hover:bg-red-500 hover:text-white font-bold uppercase tracking-wide px-4 py-2.5 text-xs flex items-center gap-2 transition-colors active:scale-[0.98]"
        data-testid="delete-account-open-btn"
      >
        <Trash size={14} weight="bold" />
        Delete my account
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center px-4"
          data-testid="delete-account-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md bg-[#161616] border border-red-500/40 p-6 relative">
            <button
              onClick={close}
              disabled={busy}
              className="absolute top-3 right-3 text-[#71717A] hover:text-white disabled:opacity-30"
              data-testid="delete-account-close-btn"
              aria-label="Close"
            >
              <X size={18} weight="bold" />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-red-500/20 text-red-400">
                <Warning size={20} weight="bold" />
              </div>
              <h3
                className="text-xl font-bold tracking-tight uppercase text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Delete account
              </h3>
            </div>
            <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4">
              This will permanently remove every workout, template, body measurement,
              personal record and preference associated with{" "}
              <span className="text-white font-semibold">
                {profile?.email || profile?.name || "this account"}
              </span>
              . The action is immediate and irreversible.
            </p>
            <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">
              Type <span className="text-red-300 font-bold">DELETE</span> to confirm
            </p>
            <input
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.toUpperCase())}
              disabled={busy}
              autoFocus
              className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2.5 text-sm focus:border-red-500 outline-none transition-all mb-4 font-mono tracking-widest"
              placeholder="DELETE"
              data-testid="delete-account-confirm-input"
            />
            <div className="flex gap-2">
              <button
                onClick={close}
                disabled={busy}
                className="flex-1 border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#3f3f46] font-bold uppercase tracking-wide py-2.5 text-xs transition-colors disabled:opacity-40"
                data-testid="delete-account-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={busy || confirm !== "DELETE"}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-30 disabled:hover:bg-red-500 text-white font-bold uppercase tracking-wide py-2.5 text-xs flex items-center justify-center gap-2 transition-colors"
                data-testid="delete-account-confirm-btn"
              >
                {busy ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Trash size={14} weight="bold" />
                    Permanently delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
